import { Elements } from "@stripe/react-stripe-js";
import { getBankRoutingScheme } from "@/helpers/bankRoutingSchemes";
import { loadStripe } from "@stripe/stripe-js";
import { useMemo, useState } from "react";
import { useAppAbility } from "@/casl";
import { Alert, Skeleton } from "@/components/atoms";
import {
  StripeConfigurationPanel,
  BankDetailsDebitsPanel,
  BankDetailsDepositsPanel,
  AccountOwnerAccessRequiredModal,
} from "@/components/organisms";
import { useCountryConfig, useOperator } from "@/hooks";
import { BankDetailsDebitsHistory } from "./BankDetailsDebitsHistory";
import { BankDetailsDepositsHistory } from "./BankDetailsDepositsHistory";

export const PaymentConfiguration = () => {
  const ability = useAppAbility();
  const { country } = useOperator();
  const { config, isLoading: isCountryLoading } = useCountryConfig(country);
  const [isModalOpen, setIsModalOpen] = useState(true);

  // The per-country publishable key comes from the country row, not from the account-management
  // keys endpoint this page used to call. That endpoint returns the key, but to do so it also mints
  // a card-verification PaymentIntent and a SetupIntent, both carrying `on_behalf_of` — so for a
  // provider with no connected Stripe account it 500s, and this page rendered a skeleton forever
  // with StripeConfigurationPanel (the only thing that creates that account) trapped behind the
  // gate. The page needed one public string and was paying for two Stripe writes to get it.
  // There is still no client-side fallback, so a non-AU provider can never be handed the wrong
  // silo's key.
  const publishableKey = config.stripePublishableKey;
  const stripePromise = useMemo(() => {
    return publishableKey ? loadStripe(publishableKey) : null;
  }, [publishableKey]);

  if (!ability.can("manage", "providerPaymentsConfig"))
    return <AccountOwnerAccessRequiredModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />;

  // Only until the country row lands — `stripePublishableKey` is deliberately not bundled, so its
  // absence is ambiguous while the request is in flight and meaningful once it has answered.
  if (isCountryLoading) return <Skeleton />;

  return (
    <div className="flex flex-col gap-y-6">
      {/* Outside the Stripe gate on purpose: it needs no Elements context, and it is the panel that
          creates the operator's Connect account on mount. Gating it on anything Stripe-shaped is
          what made a provider without one unable to reach the screen that would give them one. */}
      <StripeConfigurationPanel />
      {/* Elements renders no DOM of its own, so its children stay direct children of the flex
          column above. The bank editors below it (DebitsEditor, PlatformFeeCardEditor) call
          useStripe/useElements, which is why these two still need the provider.

          Still never mounted without a resolved key: <Elements> latches onto the first `stripe`
          instance it is given, so mounting against null would make us_bank_account setup intents
          (created on the provider's silo) fail with a 404 on link_account_sessions. That is why
          this is a conditional mount rather than a nullable `stripe` prop. */}
      {stripePromise ? (
        <Elements stripe={stripePromise} key={publishableKey}>
          {/* Deposits are collected separately only where Stripe does not handle them (BSB/sort-code
              countries); the US, Switzerland (IBAN) and NZ collect them through Stripe onboarding. */}
          {!getBankRoutingScheme(config.bankRoutingScheme).depositsViaStripeOnboarding && <BankDetailsDepositsPanel />}
          <BankDetailsDebitsPanel />
        </Elements>
      ) : (
        // Says so rather than rendering nothing. This is reachable today: NZ has a payments row with
        // vendor `stripe` and no publishable key, so its bank panels cannot mount. Silently dropping
        // them would look identical to a country that has none by design.
        <Alert
          type="warning"
          message={`Bank details cannot be set up${config.name ? ` for ${config.name}` : ""} yet — no Stripe publishable key is configured for this country. Stripe Configuration above is unaffected.`}
        />
      )}
    </div>
  );
};

PaymentConfiguration.DebitsHistory = BankDetailsDebitsHistory;
PaymentConfiguration.DepositsHistory = BankDetailsDepositsHistory;
